import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import Modal from '@mui/material/Modal';

import { Box, Tabs, Tab } from '@mui/material';
import CheckSheet from 'src/screen/Protected/DeviceScreen/TestingReport/CheckSheet';
import CommissioningSignOff from 'src/screen/Protected/DeviceScreen/Commissioning/CommissioningSignOff';
import Button from 'src/component/UI/Button';
import { getDeviceById } from 'src/module/device/selector';
import { Map, fromJS } from 'immutable';
import { getAbilities } from 'src/module/authentication/selector';
import { fetchCheckSheet, saveCheckSheet, updateCheckSheet } from 'src/module/checkSheet/action';
import { getCheckSheetReport, getCheckSheetTasks, getCheckSheetTasksLoading, getSaveCheckSheetReportLoading } from 'src/module/checkSheet/selector';
import LogoSpinner from 'src/component/UI/LogoSpinner';
import useMediaQuery from '@mui/material/useMediaQuery';
import variables from 'src/style/variable/variables.module.scss';
import { useTranslation } from 'src/i18n';

const selectGeneralTasks = getCheckSheetTasks('commissioning-comm-loop-general');

export default function CommissioningCommLoopReport (props) {
  const {
    deviceId,
    reportId,
    open
  } = props;

  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [tabValue, setTabValue] = useState("1");
  const generalTasks = useSelector(selectGeneralTasks);
  const report = useSelector(getCheckSheetReport(reportId));
  const device = useSelector(getDeviceById(deviceId));
  const isSaveLoading = useSelector(getSaveCheckSheetReportLoading('commissioning-comm-loop'));
  const isLoadingTasks = useSelector(getCheckSheetTasksLoading);
  const isMobile = useMediaQuery(`(max-width: ${variables.mobileWidth})`);
  const abilities: any = useSelector(getAbilities);
  const isPhoneView = useMediaQuery(`(max-width: ${variables.phoneWidth})`);

  const canEdit = abilities.includes('edit-check-sheet-report');
  const readOnly = Boolean(reportId) && !canEdit;

  const buildEmptyResults = (tasks) => {
    const tasksList = tasks.toJS ? tasks.toJS() : [];
    return fromJS({
      info: {
        type: 'commissioning-comm-loop',
        deviceId: deviceId || null,
      },
      'commissioning-comm-loop-general': tasksList.map((task: any) => ({
        task_id: task.key,
        status: null,
        comment: '',
      })),
      signOff: {
        recommendations: '',
        commissioningStatus: '',
      },
    }) as Map<string, any>;
  };

  const [allResults, setAllResults] = useState(Map({}));
  const [submitAttempted, setSubmitAttempted] = useState(false);

  // When tasks load for a new form, initialize allResults
  useEffect(() => {
    if (!reportId && generalTasks && generalTasks.size > 0) {
      setAllResults(buildEmptyResults(generalTasks));
    }
  }, [generalTasks, reportId]);

  // When editing an existing report, load it
  useEffect(() => {
    if (reportId) {
      dispatch(fetchCheckSheet(reportId, 'commissioning-comm-loop-general'));
    }
  }, [reportId]);

  useEffect(() => {
    if (reportId && report.get('checkSheet')) {
      setAllResults(Map(report));
    }
  }, [report, reportId]);

  if (!open) {
    return null;
  }

  if (reportId && !report.get('checkSheet')) {
    return null;
  }

  const handleTabChange = (_event: React.SyntheticEvent, newValue: string) => {
    setTabValue(newValue);
  };

  const statusError = submitAttempted && !allResults.getIn(['signOff', 'commissioningStatus']);

  const next = () => setTabValue((parseInt(tabValue) + 1).toString());
  const previous = () => setTabValue((parseInt(tabValue) - 1).toString());

  const submit = () => {
    if (!allResults.getIn(['signOff', 'commissioningStatus'])) {
      setSubmitAttempted(true);
      setTabValue('2');
      return;
    }
    const values: any = allResults.toJS();
    const c = [...(values['commissioning-comm-loop-general'] || [])];
    let payload = allResults
      .setIn(['info', 'deviceId'], deviceId || null)
      .set('checkSheet', fromJS(c))
      .set('signOff', allResults.get('signOff'));
    if (reportId) {
      payload = payload.set('id', reportId);
      dispatch(updateCheckSheet(payload, deviceId, 'commissioning-comm-loop'));
    } else {
      dispatch(saveCheckSheet('commissioning-comm-loop', payload, deviceId));
    }
  };

  const change = (path, value) => {
    setAllResults(prev => prev.setIn(path, value));
  };

  const titleTag = device ? device.get('tag') : null;

  return (
    <Modal open={props.open} onClose={props.handleClose} style={{ height: '90vh' }}>
      <div className={`modal testing-report-modal commissioning-modal${isPhoneView ? ' modal-no-padding-sides' : ''}`}>
        {!isMobile && (
          <h2>{t('commissioningCommLoop.reportTitle')} {titleTag && <b>({titleTag})</b>}</h2>
        )}
        {isLoadingTasks ? (
          <div style={{ margin: 'auto', marginTop: '200px', width: '100px' }}>
            <LogoSpinner size={100} />
          </div>
        ) : (
          <>
            <Box sx={{ width: '100%', marginBottom: '20px' }}>
              <Tabs
                value={tabValue}
                onChange={handleTabChange}
                textColor='primary'
                indicatorColor='primary'
                aria-label="commissioning-comm-loop-tabs"
                variant="scrollable"
                scrollButtons="auto"
              >
                <Tab value="1" label={<span className='tab-label-size'>{t('commissioningCommLoop.reportTitle')}</span>} />
                <Tab value="2" label={<span className='tab-label-size'>{t('commissioningCommLoop.signOffAndComments')}</span>} />
              </Tabs>
            </Box>

            {tabValue === '1' && (
              <CheckSheet
                readOnly={readOnly}
                onChange={change}
                id='commissioning-comm-loop-general'
                title={t('commissioningCommLoop.reportTitle')}
                data={allResults?.get('commissioning-comm-loop-general')}
                checkSheetType="commissioning-comm-loop-general"
              />
            )}
            {tabValue === '2' && (
              <CommissioningSignOff
                readOnly={readOnly}
                data={allResults?.get('signOff')}
                onChange={change}
                statusError={statusError}
              />
            )}

            <div className='button-bar stay-at-bottom'>
              <Button onClick={() => props.handleClose()}>{t('common.cancel')}</Button>
              {tabValue !== '1' && <Button onClick={previous}>{t('common.previous')}</Button>}
              {tabValue === '2'
                ? <Button onClick={submit} disabled={readOnly} loading={isSaveLoading} cta>{reportId ? t('common.save') : t('common.submit')}</Button>
                : <Button className='next-button' onClick={next} cta>{t('common.next')}</Button>
              }
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}

CommissioningSignOff.propTypes = {
  data: PropTypes.object,
  onChange: PropTypes.func.isRequired,
  readOnly: PropTypes.bool,
  statusError: PropTypes.bool,
};

CommissioningSignOff.defaultProps = {
  data: null,
  readOnly: false,
  statusError: false,
};

CommissioningCommLoopReport.propTypes = {
  handleClose: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
  deviceId: PropTypes.string,
  reportId: PropTypes.string,
};
